import { t } from '../../shared/config/translations'
import {
	overlayContentTemplate,
	overlayExternalLinkTemplate,
} from './overlayTemplates'

export interface OverlayView {
	path?: string
	html: string
	onMount?: () => void
	options?: OverlayOptions
}

export interface OverlayOptions {
	expiresAt?: number
	backLabel?: string
	link?:
		| false
		| {
				text?: string
				url?: string
		  }
}

type OverlayEntry = {
	view: OverlayView
}

class OverlayController {
	private readonly rootEl: HTMLElement
	private readonly appRootEl: HTMLElement | null
	private readonly overlayEl: HTMLElement | null
	private readonly overlayNavBackButton: HTMLElement | null
	private activeEntry: OverlayEntry | null = null
	private slide: Animation | null = null
	private readonly contentStack: OverlayEntry[] = []
	private eventsController: AbortController | null = null
	private expiryTimer?: ReturnType<typeof setTimeout>
	private expiresAt = Number.POSITIVE_INFINITY

	constructor(rootEl: HTMLElement) {
		this.rootEl = rootEl
		this.appRootEl = document.getElementById('app-root')
		this.overlayEl = rootEl.querySelector('#overlay-content')
		this.overlayNavBackButton = document.getElementById(
			'overlay-nav-back-btn',
		)

		this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
		this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this)

		if (!this.appRootEl || !this.overlayEl) return
	}

	isFor(rootEl: HTMLElement): boolean {
		return this.rootEl === rootEl
	}
	open(view: OverlayView): void {
		if (!this.overlayEl || !this.appRootEl) return

		const alreadyOpen = this.appRootEl.classList.contains('overlay-active')
		this.recordPath(view)
		this.activeEntry = { view }
		clearTimeout(this.expiryTimer)
		this.expiresAt = Number.POSITIVE_INFINITY
		this.contentStack.length = 0

		if (!alreadyOpen) {
			// Reopening mid-close slides back from where the panel currently is.
			const closing = !this.rootEl.hidden
			const from = closing
				? getComputedStyle(this.overlayEl).transform
				: 'translateX(100%)'
			this.slide?.cancel()
			if (!closing) {
				// Slide in without scrolling the document: a scroll jump would
				// resize Safari's toolbar mid-motion. Scrolling swaps at the end.
				this.freeze(this.rootEl, 0, window.scrollY)
				this.rootEl.hidden = false
			}
			this.appRootEl.classList.add('overlay-active')
			this.animateSlide(from, 'translateX(0)', () => this.settleOpen())
		}

		requestAnimationFrame(() => {
			if (this.activeEntry?.view === view)
				this.overlayNavBackButton?.classList.add('is-visible')
		})

		this.scrollOverlayToTop()
		const backLabel = view.options?.backLabel ?? t('nav.back')
		const linkHtml = renderExternalLink(view.options)

		this.overlayEl.innerHTML = overlayContentTemplate(
			view.html,
			linkHtml,
			backLabel,
		)
		this.bindOverlayEvents()
		view.onMount?.()
		this.limitValidity(view.options?.expiresAt)

		const contentEl = this.overlayEl.querySelector<HTMLElement>(
			'.overlay-inner-content',
		)
		requestAnimationFrame(() => {
			contentEl?.classList.add('overlay-fade--visible')
		})
	}

	/** Pushes a new overlay body so back returns to the previous body first. */
	pushContent(view: OverlayView): void {
		if (!this.overlayEl || !this.appRootEl) return

		const contentEl = this.overlayEl.querySelector<HTMLElement>(
			'.overlay-inner-content',
		)
		if (
			!contentEl ||
			!this.appRootEl.classList.contains('overlay-active') ||
			!this.activeEntry
		) {
			this.open(view)
			return
		}

		this.contentStack.push(this.activeEntry)
		this.recordPath(view)
		this.activeEntry = { view }
		this.renderActiveContent(contentEl, view)
		this.limitValidity(view.options?.expiresAt)
	}

	/** Restores the previous overlay body or closes when no body history remains. */
	back(): void {
		if (this.activeEntry?.view.path && history.state?.overlayBack) {
			history.back()
			return
		}
		const previousEntry = this.contentStack.pop()
		const contentEl = this.overlayEl?.querySelector<HTMLElement>(
			'.overlay-inner-content',
		)

		if (!previousEntry || !contentEl) {
			this.close()
			return
		}

		this.activeEntry = previousEntry
		this.renderActiveContent(contentEl, previousEntry.view)
	}

	/** Renders the overlay body and rebinds its content controller. */
	private renderActiveContent(
		contentEl: HTMLElement,
		view: OverlayView,
	): void {
		if (!this.overlayEl) return

		this.scrollOverlayToTop()
		this.updateExternalLink(view.options)
		contentEl.classList.remove('overlay-fade--visible')
		contentEl.innerHTML = view.html
		view.onMount?.()

		requestAnimationFrame(() => {
			contentEl.classList.add('overlay-fade--visible')
		})
	}

	private recordPath(view: OverlayView): void {
		if (!view.path || window.location.pathname === view.path) return
		history.pushState(
			{ overlayBack: window.location.pathname },
			'',
			view.path,
		)
	}

	close(updatePath = true): void {
		const path = this.activeEntry?.view.path
		if (updatePath && path && window.location.pathname === path) {
			const parent = path.startsWith('/setting/')
				? '/settings'
				: path.startsWith('/activity/')
					? '/activity'
					: '/collection'
			history.replaceState(null, '', parent)
		}
		clearTimeout(this.expiryTimer)
		this.expiresAt = Number.POSITIVE_INFINITY
		if (
			!this.overlayEl ||
			!this.appRootEl?.classList.contains('overlay-active')
		)
			return

		this.eventsController?.abort()
		this.eventsController = null
		this.activeEntry = null
		this.contentStack.length = 0

		// The panel slides away at its current scroll; the frozen page behind
		// it is pinned to the viewport until the swap at the end.
		const page = this.getPage()
		if (page?.classList.contains('overlay-frozen'))
			page.style.setProperty(
				'--overlay-frozen-top',
				`${window.scrollY}px`,
			)

		this.overlayNavBackButton?.classList.remove('is-visible')
		this.appRootEl.classList.remove('overlay-active')
		this.animateSlide(
			getComputedStyle(this.overlayEl).transform,
			'translateX(100%)',
			() => this.settleClose(),
		)
		document.dispatchEvent(new CustomEvent('tunetoss:overlay-close'))
	}

	/** Hands document scrolling to the fully open panel. */
	private settleOpen(): void {
		if (!this.rootEl.classList.contains('overlay-frozen')) return
		this.freeze(this.getPage(), window.scrollY)
		const overlayScrollY = this.unfreeze(this.rootEl) ?? 0
		window.scrollTo({ top: overlayScrollY, behavior: 'instant' })
	}

	/** Hands document scrolling back to the page once the panel is gone. */
	private settleClose(): void {
		const scrollY = this.unfreeze(this.getPage()) ?? window.scrollY
		this.unfreeze(this.rootEl)
		this.rootEl.hidden = true
		if (this.overlayEl) this.overlayEl.innerHTML = ''
		window.scrollTo({ top: scrollY, behavior: 'instant' })
	}

	private scrollOverlayToTop(): void {
		if (this.rootEl.classList.contains('overlay-frozen')) {
			this.rootEl.style.setProperty('--overlay-frozen-scroll', '0px')
			return
		}
		window.scrollTo({ top: 0, behavior: 'instant' })
		const page = this.getPage()
		if (page?.classList.contains('overlay-frozen'))
			page.style.setProperty('--overlay-frozen-top', '0px')
	}

	/** Pins a layer to the viewport while keeping its own scroll offset. */
	private freeze(layer: HTMLElement | null, scrollY: number, top = 0): void {
		if (!layer) return
		layer.style.setProperty('--overlay-frozen-top', `${top}px`)
		layer.style.setProperty('--overlay-frozen-scroll', `${scrollY}px`)
		layer.classList.add('overlay-frozen')
	}

	/** Returns a layer to document flow and reports its frozen offset. */
	private unfreeze(layer: HTMLElement | null): number | null {
		if (!layer?.classList.contains('overlay-frozen')) return null
		const scrollY = getFrozenScroll(layer)
		layer.classList.remove('overlay-frozen')
		layer.style.removeProperty('--overlay-frozen-top')
		layer.style.removeProperty('--overlay-frozen-scroll')
		return scrollY
	}

	private getPage(): HTMLElement | null {
		return document.getElementById('content')
	}

	private animateSlide(from: string, to: string, finish?: () => void): void {
		if (!this.overlayEl) return
		const slide = this.overlayEl.animate(
			[{ transform: from }, { transform: to }],
			{
				duration: matchMedia('(prefers-reduced-motion: reduce)').matches
					? 0
					: 300,
				easing: 'ease-in-out',
			},
		)
		this.slide = slide
		void slide.finished.then(
			() => {
				if (this.slide !== slide) return
				this.slide = null
				finish?.()
			},
			() => {},
		)
	}
	limitValidity(deadline?: number): void {
		if (!deadline || !Number.isFinite(deadline)) return
		this.expiresAt = Math.min(this.expiresAt, deadline)
		clearTimeout(this.expiryTimer)
		if (this.expiresAt <= Date.now()) {
			this.close()
			return
		}
		this.expiryTimer = setTimeout(
			() => this.close(),
			this.expiresAt - Date.now(),
		)
	}

	checkValidity(): void {
		if (this.expiresAt <= Date.now()) this.close()
	}

	private bindOverlayEvents(): void {
		this.eventsController?.abort()
		this.eventsController = new AbortController()
		const { signal } = this.eventsController
		const overlayHeaderBackButton =
			this.overlayEl?.querySelector<HTMLElement>('button.back')

		overlayHeaderBackButton?.addEventListener(
			'click',
			this.handleBackButtonClick,
			{ signal },
		)
		this.overlayNavBackButton?.addEventListener(
			'click',
			this.handleBackButtonClick,
			{ signal },
		)
		document.addEventListener('keydown', this.handleGlobalKeydown, {
			signal,
		})
	}

	/** Updates the optional external link while preserving bound back buttons. */
	private updateExternalLink(options?: OverlayOptions): void {
		const headerEl = this.overlayEl?.querySelector<HTMLElement>(
			'.overlay-header-actions',
		)
		if (!headerEl) return

		const existingLink = headerEl.querySelector<HTMLElement>('.link')
		if (options?.link === false) {
			existingLink?.remove()
			return
		}

		const linkHtml = renderExternalLink(options)
		if (existingLink) {
			existingLink.outerHTML = linkHtml
			return
		}
		headerEl.insertAdjacentHTML('beforeend', linkHtml)
	}

	private handleBackButtonClick(evt: MouseEvent): void {
		evt.preventDefault()
		this.back()
	}

	private handleGlobalKeydown(event: KeyboardEvent): void {
		if (event.key === 'ArrowLeft' || event.key === 'Escape') {
			this.back()
		}
	}
}

/** Opens a full overlay session with header actions and content. */
export function openOverlay(view: OverlayView): void {
	const controller = getOverlayController()
	if (!controller) return
	controller.open(view)
}

/** Pushes a body onto the active overlay so back returns to previous content. */
export function pushOverlayContent(view: OverlayView): void {
	const controller = getOverlayController()
	if (!controller) return
	controller.pushContent(view)
}

export function closeOverlay(updatePath = true): void {
	overlayController?.close(updatePath)
}

export function limitOverlayValidity(deadline?: number): void {
	overlayController?.limitValidity(deadline)
}
export function checkOverlayValidity(): void {
	overlayController?.checkValidity()
}

let overlayController: OverlayController | null = null

function getOverlayController(): OverlayController | null {
	const rootEl = document.getElementById('overlay-root')
	if (!rootEl) return null
	if (!overlayController || !overlayController.isFor(rootEl)) {
		overlayController = new OverlayController(rootEl)
	}
	return overlayController
}

/** Reads the saved scroll offset of a layer frozen behind or under motion. */
export function getFrozenScroll(layer: HTMLElement): number {
	return Number.parseFloat(
		layer.style.getPropertyValue('--overlay-frozen-scroll') || '0',
	)
}

/** Renders the optional overlay external link action. */
function renderExternalLink(options?: OverlayOptions): string {
	if (options?.link === false) return ''
	return overlayExternalLinkTemplate(
		options?.link?.url ?? '#',
		options?.link?.text ?? t('nav.showOnDiscogs'),
	)
}
